import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { adminApi } from '../../api/admin.api';
import { AttendanceRecordItem } from '../../types/Admin.api.type';

const PERIOD_OPTIONS = [
  { value: 'TODAY', label: '오늘' },
  { value: '7D', label: '7일' },
  { value: '30D', label: '30일' },
  { value: 'ALL', label: '전체' },
] as const;

export const AttendanceManagement: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [mobileVisiblePages, setMobileVisiblePages] = useState(1);
  const [conversionPeriod, setConversionPeriod] = useState('TODAY');
  const pageSize = 20;

  const { data, isLoading } = useQuery({
    queryKey: ['adminAttendanceRecords', currentPage],
    queryFn: () => adminApi.getAttendanceRecords(currentPage, pageSize).then(res => res.data),
  });

  const items: AttendanceRecordItem[] = data?.content ?? [];
  const totalPages = data?.totalPages ?? 0;
  const totalElements = data?.totalElements ?? 0;

  // 모바일: 여러 페이지 데이터 누적 조회
  const { data: mobileData } = useQuery({
    queryKey: ['adminAttendanceRecordsMobile', mobileVisiblePages],
    queryFn: async () => {
      const promises = Array.from({ length: mobileVisiblePages }, (_, i) =>
        adminApi.getAttendanceRecords(i, pageSize).then(res => res.data)
      );
      const results = await Promise.all(promises);
      return {
        content: results.flatMap(r => r.content),
        totalElements: results[0]?.totalElements ?? 0,
      };
    },
  });

  const mobileItems = mobileData?.content ?? [];
  const mobileTotalElements = mobileData?.totalElements ?? 0;

  const { data: conversionStats } = useQuery({
    queryKey: ['attendanceConversionStats', conversionPeriod],
    queryFn: () => adminApi.getAttendanceConversionStats(conversionPeriod).then(res => res.data),
  });

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <div className="p-6 text-center">로딩 중...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm p-4 lg:p-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-lg lg:text-xl font-bold text-[#030213]">출석체크</h2>
            <p className="text-sm text-[#6B7280]">총 {totalElements}건</p>
          </div>
          <div className="flex gap-1">
            {PERIOD_OPTIONS.map(opt => (
              <button
                key={opt.value}
                onClick={() => setConversionPeriod(opt.value)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  conversionPeriod === opt.value
                    ? 'bg-[#030213] text-white'
                    : 'bg-[#f3f3f5] text-[#6B7280] hover:bg-[#E5E7EB]'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
        {conversionStats ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-3 border-t border-[#E5E7EB]">
            <div>
              <p className="text-xs text-[#6B7280]">출석 수</p>
              <p className="text-lg font-bold text-[#030213]">{conversionStats.attendanceCount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">광고 시청</p>
              <p className="text-lg font-bold text-[#030213]">{conversionStats.adWatchCount.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">전환율</p>
              <p className={`text-lg font-bold ${conversionStats.conversionRate >= 50 ? 'text-green-600' : conversionStats.conversionRate >= 30 ? 'text-yellow-600' : 'text-red-500'}`}>
                {conversionStats.conversionRate}%
              </p>
            </div>
            <div>
              <p className="text-xs text-[#6B7280]">연속유지 광고</p>
              <p className="text-lg font-bold text-purple-600">{conversionStats.streakSaveCount.toLocaleString()}</p>
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#6B7280] pt-3 border-t border-[#E5E7EB]">로딩 중...</div>
        )}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#f3f3f5]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">#</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-[#6B7280] uppercase tracking-wider">사용자</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">출석일</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">연속일수</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">유형</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-[#6B7280] uppercase tracking-wider">광고시청</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-[#6B7280] uppercase tracking-wider">시간</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-[#E5E7EB]">
              {items.map((item, idx) => (
                <tr key={item.id} className="hover:bg-[#f3f3f5] transition-colors">
                  <td className="px-4 py-4 text-sm font-medium text-[#6B7280]">
                    {currentPage * pageSize + idx + 1}
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-[#f3f3f5] flex items-center justify-center overflow-hidden">
                        {item.profileImageUrl ? (
                          <img src={item.profileImageUrl} alt={item.userName} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <span className="text-[#6B7280] font-medium text-xs">
                            {item.userName?.charAt(0)?.toUpperCase() ?? '?'}
                          </span>
                        )}
                      </div>
                      <span className="text-sm font-medium text-[#030213]">{item.userName}</span>
                    </div>
                  </td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.attendanceDate}</td>
                  <td className="px-4 py-4 text-center text-sm text-[#030213]">{item.streakCount}일</td>
                  <td className="px-4 py-4 text-center">
                    {item.streakSaved ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-yellow-100 text-yellow-700">광고유지</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-700">일반출석</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-center">
                    {item.adWatched ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">시청</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">미시청</span>
                    )}
                  </td>
                  <td className="px-4 py-4 text-right text-sm text-[#6B7280]">
                    {formatDate(item.regDateTime)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 p-4 border-t border-[#E5E7EB]">
            <button
              onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
              disabled={currentPage === 0}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >이전</button>
            <span className="text-sm text-[#6B7280]">{currentPage + 1} / {totalPages}</span>
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages - 1, prev + 1))}
              disabled={currentPage === totalPages - 1}
              className="px-3 py-1 text-sm border border-[#E5E7EB] rounded hover:bg-[#f3f3f5] disabled:opacity-50 disabled:cursor-not-allowed"
            >다음</button>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {mobileItems.map((item, idx) => (
          <div key={item.id} className="bg-white rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-[#f3f3f5] flex items-center justify-center text-xs font-bold text-[#6B7280]">
                {idx + 1}
              </div>
              <div className="w-8 h-8 rounded-full bg-[#f3f3f5] flex items-center justify-center overflow-hidden">
                {item.profileImageUrl ? (
                  <img src={item.profileImageUrl} alt={item.userName} className="w-8 h-8 rounded-full object-cover" />
                ) : (
                  <span className="text-[#6B7280] font-medium text-xs">
                    {item.userName?.charAt(0)?.toUpperCase() ?? '?'}
                  </span>
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-[#030213]">{item.userName}</span>
                  <div className="flex items-center gap-1">
                    {item.adWatched ? (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-700">광고시청</span>
                    ) : (
                      <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500">미시청</span>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-[#6B7280]">
                  <span>{item.attendanceDate}</span>
                  <span>연속 {item.streakCount}일</span>
                  <span>{item.streakSaved ? '광고유지' : '일반출석'}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
        {mobileVisiblePages * pageSize < mobileTotalElements && (
          <button
            onClick={() => setMobileVisiblePages(prev => prev + 1)}
            className="w-full py-3 text-sm font-medium text-[#030213] bg-white border border-[#E5E7EB] rounded-lg hover:bg-[#f3f3f5] transition-colors"
          >더 보기</button>
        )}
      </div>
    </div>
  );
};
